import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, gql } from 'urql'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card'
import { Logo } from '../components/logo'

const REQUEST_PASSWORD_RESET_MUTATION = gql(`
  mutation RequestPasswordReset($email: String!) {
    requestPasswordReset(email: $email)
  }
`)

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [{ fetching }, requestReset] = useMutation(REQUEST_PASSWORD_RESET_MUTATION)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')
    const result = await requestReset({ email })
    if (result.error) {
      setError(result.error.graphQLErrors[0]?.message ?? result.error.message)
      return
    }
    setSubmitted(true)
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-primary/5 dark:bg-gray-900 p-4 gap-8">
      <div className="scale-150">
        <Logo />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Forgot password?</CardTitle>
          <CardDescription>
            {submitted
              ? 'Check your email for a reset link.'
              : "Enter your email and we'll send you a reset link."}
          </CardDescription>
        </CardHeader>
        {!submitted && (
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {error && (
                <div className="text-sm text-red-500 font-medium">{error}</div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </CardContent>
            <CardFooter>
              <Button type="submit" className="w-full" disabled={fetching}>
                {fetching ? 'Sending...' : 'Send reset link'}
              </Button>
            </CardFooter>
          </form>
        )}
        <CardFooter className={submitted ? 'pt-0' : 'pt-0 flex justify-center'}>
          <Button variant="link" size="sm" asChild>
            <Link to="/login">Back to sign in</Link>
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}
